import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ensureHubSpotScript, createHubSpotForm, HUBSPOT_LIGHT_CSS } from "@/lib/hubspot";

const HubSpotContactForm = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!containerRef.current) return;
      containerRef.current.innerHTML = "";
      ensureHubSpotScript(() => {
        if (!containerRef.current) return;
        containerRef.current.innerHTML = `<div id="contact-hs-form"></div>`;
        createHubSpotForm({ region: "eu1", portalId: "25538277", formId: "1abc3c85-de83-4f29-b20a-39e9e1c482a3", target: "#contact-hs-form", css: HUBSPOT_LIGHT_CSS });
      });
    }, 100);
    return () => clearTimeout(timeout);
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}>
      <div ref={containerRef} className="min-h-[200px] [&_.hs-form-frame]:w-full" />
    </motion.div>
  );
};

const ease = [0.16, 1, 0.3, 1] as const;

const Contact = () => {
  const { t, i18n } = useTranslation('contact');

  const offices = [
    { title: t('swedenHq'), address: t('swedenAddress') },
    { title: t('canadaSub'), address: t('canadaAddress') },
    { title: t('singaporeSub'), address: t('singaporeAddress') },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href="https://cetasol.com/contact" />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cetasol.com/contact" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://cetasol.com/contact" }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="pt-24 pb-0">
        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-5xl mb-24">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
                <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6 leading-[1.1]">
                  {t('heading')}{" "}<span className="text-foreground">{t('headingSpan')}</span>
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed mb-10">{t('description')}</p>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Mail className="text-foreground mt-1 shrink-0" size={20} />
                    <div>
                      <h3 className="font-display font-semibold text-foreground mb-1">{t('emailLabel')}</h3>
                      <a href="mailto:info@cetasol.com" className="text-muted-foreground hover:text-foreground transition-colors">info@cetasol.com</a>
                    </div>
                  </div>
                </div>
              </motion.div>
              <HubSpotContactForm />
            </div>
          </div>
        </section>

        <section className="section-fade py-20">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-8">{t('officesHeading')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-10">
                {offices.map((office) => (
                  <div key={office.title} className="border-l border-border pl-6">
                    <MapPin className="text-muted-foreground mb-3" size={20} />
                    <h3 className="font-display font-semibold text-foreground mb-1">{office.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{office.address}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
